import { Box } from "@chakra-ui/react";
import type { BoxProps } from "@chakra-ui/react";
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

/**
 * Matches [[analysis: some name]] tags in stored text.
 * These tags are semantic markers that go to the agent as-is.
 * In the editor they are rendered as a styled non-editable purple chip.
 */
const ANALYSIS_TAG_RE = /\[\[analysis:\s*([^\]]+)\]\]/gi;

/**
 * Browsers (especially Chrome) inject zero-width chars (U+200B, U+FEFF)
 * around contentEditable="false" nodes. Strip them when reading back text.
 */
const ZERO_WIDTH_RE = /[\u200B\uFEFF]/g;

/**
 * Walk the contenteditable div and reconstruct the canonical raw text string,
 * including `[[analysis: name]]` tags, from the live DOM.
 *
 * This is the inverse of `buildNodes`. It is called:
 *  - on every `input` event, to derive the new `value` to hand to `onChange`
 *  - inside the `useEffect` sync guard, to decide whether a re-render is needed
 *
 * Node handling rules:
 *
 *  TEXT_NODE  → append its text content directly, stripping zero-width chars
 *               (Chrome inserts U+200B / U+FEFF around contentEditable=false nodes).
 *
 *  ELEMENT_NODE / BR  → convert to a newline character, which is how the browser
 *                        represents line breaks inside a contenteditable div.
 *
 *  ELEMENT_NODE with `data-tag`  → this is an analysis chip span. Its *visual*
 *                                   textContent says "Analysis: foo", but the raw
 *                                   value we want is the original `[[analysis: foo]]`
 *                                   stored in `el.dataset.tag`. We restore that tag
 *                                   verbatim and do NOT recurse into the chip's children,
 *                                   because the SVG icon and label text must not appear
 *                                   in the serialised output.
 *
 *  Any other ELEMENT_NODE  → recurse into its children (handles divs that browsers
 *                             insert automatically on Enter in some configurations).
 */
function serializeDiv(div: HTMLDivElement): string {
	let result = "";
	const walk = (node: Node) => {
		if (node.nodeType === Node.TEXT_NODE) {
			result += (node.textContent ?? "").replace(ZERO_WIDTH_RE, "");
		} else if (node.nodeType === Node.ELEMENT_NODE) {
			const el = node as HTMLElement;
			if (el.tagName === "BR") {
				result += "\n";
			} else if (el.dataset.tag) {
				// Chip node — restore the raw tag, ignore visual textContent
				result += el.dataset.tag;
			} else {
				Array.from(el.childNodes).forEach(walk);
			}
		}
	};
	Array.from(div.childNodes).forEach(walk);
	return result;
}

/** Turn raw text into an array of DOM nodes (text nodes + chip spans). */
function buildNodes(text: string): Node[] {
	const nodes: Node[] = [];
	const re = new RegExp(ANALYSIS_TAG_RE.source, "gi");
	let lastIndex = 0;
	let match: RegExpExecArray | null;

	while ((match = re.exec(text)) !== null) {
		if (match.index > lastIndex) {
			nodes.push(document.createTextNode(text.slice(lastIndex, match.index)));
		}

		const chip = document.createElement("span");
		chip.dataset.tag = match[0];
		chip.contentEditable = "false";
		chip.style.cssText =
			"background:var(--chakra-colors-purple-50);color:var(--chakra-colors-gray-800);border-radius:4px;" +
			"padding:1px 6px;font-weight:600;white-space:nowrap;display:inline-flex;align-items:center;gap:3px;";

		const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
		svg.setAttribute("viewBox", "0 0 24 24");
		svg.setAttribute("width", "16");
		svg.setAttribute("height", "16");
		svg.style.cssText = "display:inline;vertical-align:middle;fill:currentColor;flex-shrink:0;";
		[
			"M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z",
			"M7 12h2v5H7zM15 7h2v10h-2zM11 14h2v3h-2zM11 10h2v2h-2z",
		].forEach((d) => {
			const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
			path.setAttribute("d", d);
			svg.appendChild(path);
		});

		chip.appendChild(svg);
		chip.appendChild(document.createTextNode(`Analysis: ${match[1].trim()}`));
		nodes.push(chip);

		lastIndex = match.index + match[0].length;
	}

	if (lastIndex < text.length) {
		nodes.push(document.createTextNode(text.slice(lastIndex)));
	} else if (nodes.length > 0) {
		// The text ended with a chip. Without a text node after it browsers
		// can't place the cursor past the chip, making it impossible to type
		// after it. An empty text node gives the cursor a valid landing spot.
		nodes.push(document.createTextNode(""));
	}

	return nodes;
}

/**
 * Move the browser text cursor (caret) to the very end of the contenteditable div.
 *
 * Why not just call `el.focus()`?
 * `focus()` alone restores the cursor to wherever it was when the element last lost
 * focus — which may be the beginning of the text. Using the Selection/Range API lets
 * us explicitly collapse the selection to the end of the node's contents, so the user
 * can immediately continue typing without having to click to reposition.
 *
 * Called by the imperative `focus()` handle (see `useImperativeHandle` below) whenever
 * a parent component programmatically focuses the textarea (e.g. after inserting a chip
 * via the mention menu, or when the chat input mounts).
 */
function placeCursorAtEnd(el: HTMLDivElement) {
	const sel = window.getSelection();
	if (!sel) return;
	const range = document.createRange();
	range.selectNodeContents(el);
	range.collapse(false);
	sel.removeAllRanges();
	sel.addRange(range);
}

/**
 * Imperative handle exposed to parent components via `ref`.
 *
 * Because this component is a contenteditable div (not a native <input> or
 * <textarea>), React's standard `ref.focus()` would only move browser focus
 * without repositioning the caret. The `focus` method here combines
 * `div.focus()` with `placeCursorAtEnd` so callers always get a usable cursor
 * position, not just visual focus.
 *
 * Usage:
 * ```tsx
 * const textareaRef = useRef<HighlightedTextareaHandle>(null);
 * // ...
 * textareaRef.current?.focus(); // focuses AND moves cursor to end
 * // ...
 * <HighlightedTextarea ref={textareaRef} ... />
 * ```
 */
export interface HighlightedTextareaHandle {
	focus: () => void;
}

export interface HighlightedTextareaProps
	extends Pick<
		BoxProps,
		| "fontSize"
		| "fontWeight"
		| "px"
		| "py"
		| "pt"
		| "pb"
		| "minHeight"
		| "maxHeight"
		| "flex"
		| "w"
		| "id"
	> {
	value: string;
	onChange: (value: string) => void;
	onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
	placeholder?: string;
	isDisabled?: boolean;
}

/**
 * A contenteditable div that renders [[analysis: name]] tokens as styled
 * purple chips. The raw value (including the tags) is always what is stored
 * and sent to the agent — the chips are purely visual DOM nodes.
 *
 * Unlike the old dual-layer (transparent textarea + backdrop div) approach,
 * the cursor never misaligns because there is only one layer: the cursor,
 * the text, and the chips all live in the same DOM element.
 */
const HighlightedTextarea = forwardRef<HighlightedTextareaHandle, HighlightedTextareaProps>(({
	value,
	onChange,
	onKeyDown,
	placeholder,
	isDisabled,
	fontSize = "md",
	fontWeight,
	px,
	py,
	pt,
	pb,
	minHeight,
	maxHeight,
	flex,
	w,
	id,
}, ref) => {
	const divRef = useRef<HTMLDivElement>(null);

	useImperativeHandle(ref, () => ({
		focus: () => {
			const div = divRef.current;
			if (!div) return;
			div.focus();
			placeCursorAtEnd(div);
		},
	}));

	/**
	 * Keep the DOM in sync with the controlled `value` prop.
	 *
	 * The guard (`serializeDiv(div) === value`) is critical: without it, every
	 * keystroke would tear down and rebuild the DOM from scratch. That would:
	 *   1. Reset the cursor to position 0 after every character.
	 *   2. Cause a flicker as the browser repaints the element.
	 *
	 * When the user types, `handleInput` calls `onChange(serializeDiv(div))`,
	 * which updates `value`. On the next render this effect fires, but because
	 * the DOM already reflects what the user typed, the guard short-circuits and
	 * the cursor is left untouched.
	 *
	 * A full rebuild IS performed when `value` changes from outside (e.g. the
	 * parent resets the field, or injects a `[[analysis: name]]` chip programmatically).
	 */
	useEffect(() => {
		const div = divRef.current;
		if (!div) return;
		if (serializeDiv(div) === value) return;

		div.innerHTML = "";
		const nodes = buildNodes(value);
		nodes.forEach((n) => div.appendChild(n));
	}, [value]);

	const handleInput = () => {
		if (!divRef.current) return;
		onChange(serializeDiv(divRef.current));
	};

	// Strip rich-text formatting on paste — plain text only.
	const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
		e.preventDefault();
		const text = e.clipboardData.getData("text/plain");
		document.execCommand("insertText", false, text);
	};

	const effectivePt = pt ?? py;
	const effectivePb = pb ?? py;

	return (
		<Box
			ref={divRef}
			id={id}
			flex={flex}
			w={flex ? "100%" : w}
			contentEditable={!isDisabled}
			suppressContentEditableWarning
			onInput={handleInput}
			onKeyDown={onKeyDown as React.KeyboardEventHandler<HTMLDivElement>}
			onPaste={handlePaste}
			data-placeholder={placeholder}
			fontSize={fontSize}
			fontWeight={fontWeight}
			px={px}
			pt={effectivePt}
			pb={effectivePb}
			lineHeight="1.5"
			fontFamily="inherit"
			minH={minHeight}
			maxH={maxHeight}
			overflowY="auto"
			outline="none"
			textAlign="left"
			wordBreak="break-word"
			whiteSpace="pre-wrap"
			color={isDisabled ? "gray.400" : "gray.800"}
			cursor={isDisabled ? "not-allowed" : "text"}
			sx={{
				"&:empty::before": {
					content: "attr(data-placeholder)",
					color: "var(--chakra-colors-gray-400)",
					pointerEvents: "none",
				},
				caretColor: "var(--chakra-colors-gray-800)",
			}}
		/>
	);
});

export default HighlightedTextarea;
